import { BonusStrategy, BonusType, BonusResult, AmountConstraints, ConfettiConfig } from '../interfaces/bonus.interface';
import { WheelOption } from '@interface/ruleta.interface';

/**
 * Estrategia para procesar premios de ruleta como bonos cashback
 * Maneja tanto premios fijos (50K, 10K) como porcentuales (0.5%, 0.25%)
 */
export class RuletteBonusStrategy implements BonusStrategy {
  type = BonusType.RULETA;
  campaignSpecific = true; // Bono específico con premio de ruleta

  /**
   * Calcula el bono basado en el premio de ruleta ganado
   * @param amount Monto de la inversión del usuario
   * @param extraParams Debe contener la propiedad roulettePrize con el WheelOption ganado y opcionalmente customTermsUrl
   * @returns BonusResult con el monto de cashback calculado
   */
  calculateBonus(amount: number, termDays: number, extraParams?: { roulettePrize: WheelOption; customTermsUrl?: string }): BonusResult {
    if (!extraParams?.roulettePrize) {
      return this.createInvalidResult('No se proporcionó premio de ruleta');
    }
    const prize = extraParams.roulettePrize;
    const customTermsUrl = extraParams.customTermsUrl;
    if (this.isBestRatePrize(prize)) {
      return this.calculateBestRateBonus(amount, prize, customTermsUrl);
    }
    if (this.isKeepPlayingPrize(prize)) {
      return this.calculateKeepPlayingResult(prize);
    }
    if (this.isPercentualPrize(prize)) {
      return this.calculatePercentualBonus(amount, termDays, prize, customTermsUrl);
    } else {
      return this.calculateFixedBonus(amount, prize, customTermsUrl);
    }
  }

  /**
   * Valida si el monto es válido para bonos de ruleta
   * Los bonos de ruleta no tienen restricciones de monto mínimo
   */
  validateAmount(amount: number): boolean {
    return amount > 0;
  }

  /**
   * Obtiene la configuración de confetti para premios de ruleta
   */
  getConfettiConfig(): ConfettiConfig {
    return {
      type: 'roulette',
      duration: 4000,
      intensity: 'heavy'
    };
  }

  /**
   * Los bonos de ruleta no tienen restricciones de monto
   */
  getAmountConstraints(): AmountConstraints {
    return {
      locked: false,
      minAmount: 1000000, // Mínimo para participar en ruleta
      maxAmount: undefined
    };
  }

  /**
   * Términos y condiciones específicos para bonos de ruleta
   * Detecta automáticamente los términos según el origen del bono
   */
  getTermsAndConditions(extraParams?: { roulettePrize?: WheelOption; customTermsUrl?: string }): { url: string; description: string } {
    // PRIORIDAD 1: Si se especificaron términos personalizados, usarlos
    if (extraParams?.customTermsUrl) {
      return {
        url: extraParams.customTermsUrl,
        description: `Aplican términos y condiciones específicos de la campaña`
      };
    }

    // PRIORIDAD 2: Detectar por bonus_type o id del premio
    if (extraParams?.roulettePrize) {
      const prize = extraParams.roulettePrize;
      
      // Si es un bono de Finverzo 2025
      if (prize.bonus_type?.includes('FINVERZO') || prize.id === 'finverzo_2025') {
        return {
          url: '/legal/finverzo-2025',
          description: 'Aplican términos y condiciones para ruleta Finverzo 2025'
        };
      }
      
      // Si es Mundial de Tejo 2025
      if (prize.bonus_type?.includes('MDT') || prize.id === 'mundial_tejo_2025') {
        return {
          url: '/legal/mundial-de-tejo-2025',
          description: 'Aplican términos y condiciones para Mundial de Tejo 2025'
        };
      }
    }

    // PRIORIDAD 3: Términos genéricos para otras ruletas
    return {
      url: '/terminos-y-condiciones-ruleta',
      description: 'Aplican términos y condiciones para bonos de ruleta'
    };
  }

  // ==================== MÉTODOS PRIVADOS ====================

  /**
   * Determina si es el premio especial "La Mejor tasa del mercado"
   */
  private isBestRatePrize(prize: WheelOption): boolean {
    return prize.bonus_type === 'MEJOR_TASA_MERCADO' || 
           prize.label.toLowerCase().includes('mejor tasa');
  }

  /**
   * Determina si es el premio "Sigue participando"
   */
  private isKeepPlayingPrize(prize: WheelOption): boolean {
    return prize.bonus_type === 'NO_BONUS' || 
           prize.label.toLowerCase().includes('sigue participando');
  }

  /**
   * Determina si el premio es porcentual basado en el bonus_type
   */
  private isPercentualPrize(prize: WheelOption): boolean {
    return prize.bonus_type.includes('PERCENTAGE') || prize.bonus_type.includes('PORCENTUAL');
  }

  /**
   * Calcula bono porcentual: monto * porcentaje
   */
  private calculatePercentualBonus(amount: number, termDays: number, prize: WheelOption, customTermsUrl?: string): BonusResult {
    try {
      const percentage = this.extractPercentageFromLabel(prize.label);
      const bonusAmount = Math.round(amount * (percentage / 100) * (termDays / 360));
      const terms = this.getTermsAndConditions({ roulettePrize: prize, customTermsUrl });
      return {
        isValid: true,
        bonusAmount,
        message: `¡Ganaste ${prize.label} de cashback!`,
        confettiType: 'roulette_percentage',
        displayAmount: this.formatCurrency(bonusAmount),
        bonusTag: 'ruleta-porcentual',
        message_simulador: `Cashback ${prize.label}: ${this.formatCurrency(bonusAmount)}`,
        roulettePrize: prize,
        termsAndConditionsUrl: terms.url,
        termsAndConditionsDescription: terms.description
      };
    } catch (error) {
      console.error('Error calculando bono porcentual de ruleta:', error);
      return this.createInvalidResult('Error procesando premio porcentual');
    }
  }

  /**
   * Calcula el premio especial "La Mejor tasa del mercado"
   */
  private calculateBestRateBonus(amount: number, prize: WheelOption, customTermsUrl?: string): BonusResult {
    try {
      // Obtener términos y condiciones
      const terms = this.getTermsAndConditions({ roulettePrize: prize, customTermsUrl });

      return {
        isValid: true,
        bonusAmount: 0, // No es un bono monetario, es una mejora de tasa
        message: '¡Ganaste la Mejor tasa del mercado!',
        confettiType: 'roulette_golden',
        displayAmount: 'Mejor tasa',
        bonusTag: 'ruleta-mejor-tasa',
        message_simulador: 'Premio especial: La Mejor tasa del mercado',
        roulettePrize: prize,
        termsAndConditionsUrl: terms.url,
        termsAndConditionsDescription: terms.description,
        specialPrize: true // Flag para indicar que es un premio especial
      };
    } catch (error) {
      console.error('Error procesando premio mejor tasa:', error);
      return this.createInvalidResult('Error procesando premio especial');
    }
  }

  /**
   * Maneja el resultado "Sigue participando"
   */
  private calculateKeepPlayingResult(prize: WheelOption): BonusResult {
    try {
      return {
        isValid: false, // No es un bono válido, pero tampoco es un error
        bonusAmount: 0,
        message: '¡Sigue participando!',
        confettiType: 'roulette_consolation',
        displayAmount: 'Sin premio',
        bonusTag: 'ruleta-sigue-participando',
        message_simulador: 'Resultado: Sigue participando',
        roulettePrize: prize,
        keepPlaying: true // Flag especial para "sigue participando"
      };
    } catch (error) {
      console.error('Error procesando "sigue participando":', error);
      return this.createInvalidResult('Error procesando resultado');
    }
  }

  /**
   * Calcula bono fijo: monto fijo independiente de la inversión
   */
  private calculateFixedBonus(amount: number, prize: WheelOption, customTermsUrl?: string): BonusResult {
    try {
      const bonusAmount = this.extractMoneyFromLabel(prize.label);

      // Obtener términos y condiciones
      const terms = this.getTermsAndConditions({ roulettePrize: prize, customTermsUrl });

      return {
        isValid: true,
        bonusAmount,
        message: `¡Ganaste ${this.formatCurrency(bonusAmount)} de cashback!`,
        confettiType: 'roulette_fixed',
        displayAmount: this.formatCurrency(bonusAmount),
        bonusTag: 'ruleta-fijo',
        message_simulador: `Cashback fijo: ${this.formatCurrency(bonusAmount)}`,
        roulettePrize: prize,
        termsAndConditionsUrl: terms.url,
        termsAndConditionsDescription: terms.description
      };
    } catch (error) {
      console.error('Error calculando bono fijo de ruleta:', error);
      return this.createInvalidResult('Error procesando premio fijo');
    }
  }

  /**
   * Extrae el porcentaje del label del premio
   * Ejemplos: "+0.5%" -> 0.5, "+0.25%" -> 0.25
   */
  private extractPercentageFromLabel(label: string): number {
    const match = label.match(/\+?(\d+\.?\d*)%/);
    if (!match) {
      throw new Error(`No se pudo extraer porcentaje del label: ${label}`);
    }
    return parseFloat(match[1]);
  }

  /**
   * Extrae el monto en pesos del label del premio
   * Ejemplos: "$1.000 de bonificación" -> 1000, "$500 de bonificación" -> 500, "+50 MIL" -> 50000, "+1 MILLÓN" -> 1000000
   */
  private extractMoneyFromLabel(label: string): number {
    const upperLabel = label.toUpperCase();
    
    // Primero verificar si hay un valor numérico directo en el prize.value
    // Esto es más confiable que parsear el label
    
    // Patrón para "$1.000 de bonificación", "$500 de bonificación", etc.
    const dollarMatch = label.match(/\$([0-9,.]+)/);
    if (dollarMatch) {
      const numberStr = dollarMatch[1].replace(/[,.]/g, '');
      const amount = parseInt(numberStr, 10);
      if (!isNaN(amount)) {
        console.log(`Extraído de formato dólar: ${amount} de "${label}"`);
        return amount;
      }
    }
    
    // Patrón para "2% de bonificación"
    const percentMatch = label.match(/(\d+)%/);
    if (percentMatch) {
      // Para porcentajes, retornamos el valor numérico (será manejado como porcentaje)
      const percent = parseInt(percentMatch[1], 10);
      console.log(`Extraído porcentaje: ${percent}% de "${label}"`);
      return percent; // El llamador deberá interpretar esto como porcentaje
    }
    
    // Patrón para MILLÓN: "+1 MILLÓN" -> 1000000
    const millonMatch = upperLabel.match(/(\d+)\s*MILL[OÓ]N/);
    if (millonMatch) {
      const millions = parseInt(millonMatch[1]) * 1000000;
      //console.log(`Extraído millones: ${millions} de "${label}"`);
      return millions;
    }
    
    // Patrones legacy para compatibilidad
    if (upperLabel.includes('50') && upperLabel.includes('MIL')) {
      return 50000;
    }
    
    if (upperLabel.includes('100') && upperLabel.includes('MIL')) {
      return 100000;
    }
    
    if (upperLabel.includes('20') && upperLabel.includes('MIL')) {
      return 20000;
    }
    
    if (upperLabel.includes('10') && upperLabel.includes('MIL')) {
      return 10000;
    }

    // Fallback: intentar extraer número + MIL (pero NO MILLÓN)
    const milMatch = upperLabel.match(/(\d+)\s*MIL(?![OÓ]N)/);
    if (milMatch) {
      const miles = parseInt(milMatch[1]) * 1000;
      console.log(`Extraído miles: ${miles} de "${label}"`);
      return miles;
    }

    throw new Error(`No se pudo extraer monto del label: ${label}`);
  }

  /**
   * Formatea un monto en pesos colombianos
   */
  private formatCurrency(amount: number): string {
    return amount.toLocaleString('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  /**
   * Crea un resultado inválido con mensaje de error
   */
  private createInvalidResult(message: string): BonusResult {
    return {
      isValid: false,
      bonusAmount: 0,
      message,
      confettiType: '',
      bonusTag: 'ruleta-error'
    };
  }
}